import { useHydrated, useMatches } from "@tanstack/react-router";
import { useEffect } from "react";
import type { CmsHeadAssets } from "#/_packages/umbrella/utils/cms-head-types";

const executed = new Set<string>();
const noScripts: CmsHeadAssets["scripts"] = [];
const isJsonData = ({ type }: CmsHeadAssets["scripts"][number]) =>
	type === "application/ld+json" || type === "application/json";

/** Render CMS scripts on the server and execute each only once per document. */
export function CmsHeadScripts() {
	const customHead = useMatches({
		select: (matches) =>
			(
				matches.find((match) => match.routeId === "/_umbraco/_content")
					?.loaderData as { customHead?: CmsHeadAssets } | undefined
			)?.customHead,
	});
	const scripts = customHead?.scripts ?? noScripts;
	const hydrated = useHydrated();
	useEffect(() => {
		for (const descriptor of scripts) {
			if (isJsonData(descriptor)) continue;
			const { children, ...attributes } = descriptor;
			const key = String(
				attributes.src || `${attributes.type ?? ""}:${children}`,
			);
			if (executed.has(key)) continue;
			executed.add(key);
			// During hydration these scripts have already executed from the SSR HTML.
			if (!hydrated) continue;
			const script = document.createElement("script");
			for (const [name, value] of Object.entries(attributes)) {
				if (value !== false)
					script.setAttribute(name, value === true ? "" : value);
			}
			script.textContent = children;
			document.head.appendChild(script);
		}
	}, [scripts, hydrated]);
	// JSON-LD is document metadata, not an executable once-per-document script.
	// Keep it in the head after hydration and update it when settings change.
	return (hydrated ? scripts.filter(isJsonData) : scripts).map(
		({ children, ...attributes }, index) => (
			<script
				key={String(attributes.src ?? index)}
				{...attributes}
				// biome-ignore lint/security/noDangerouslySetInnerHtml: Trusted CMS administrator scripts, matching Razor Html.Raw.
				dangerouslySetInnerHTML={{ __html: children }}
			/>
		),
	);
}
